import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Collapsible } from '../Collapsible/Collapsible'
import { Toggle } from '../Toggle/Toggle'
import { IconQuestion } from '../SVGIcons/IconQuestion'
import './Box.scss'

export const BOX_VARIANT_PRIMARY = 'primary'
export const BOX_VARIANT_SECONDARY = 'secondary'
export const BOX_VARIANT_TERTIARY = 'tertiary'

export const BOX_TRIGGER_DEFAULT = 'default'
export const BOX_TRIGGER_TOGGLE = 'toggle'

export const BOX_TITLE_BIG = 'title_big'
export const BOX_TITLE_SMALL = 'title_small'

export const Box = ({
  children,
  title,
  titleVariant,
  tooltipTitle,
  className,
  defaultExpanded,
  variant,
  trigger,
  collapsible,
  onCollapseChange,
  isClickable,
  onClickCallback,
  isDisable,
  openExpanded,
  triggerDisable,
  'data-mu-test': dataAttributeTest
}) => {
  const [collapsed, setCollapsed] = useState(!defaultExpanded)
  const [forceExpanded, setForceExpanded] = useState(openExpanded)

  const { current: collapse } = useRef(() => {
    setCollapsed(true)
    setForceExpanded(false)
    if (typeof onCollapseChange === 'function') {
      onCollapseChange(true)
    }
  })

  const { current: expand } = useRef(() => {
    setCollapsed(false)
    setForceExpanded(false)
    if (typeof onCollapseChange === 'function') {
      onCollapseChange(false)
    }
  })

  const toggle = () => {
    if (!triggerDisable) {
      if (collapsed && !forceExpanded) {
        expand()
        return
      }
      if (openExpanded) {
        setForceExpanded(false)
      }
      collapse()
    }
  }

  const box = useRef(null)
  function onClickContainer(e) {
    if (
      !e.target.dataset.muTest ||
      e.target.dataset.muTest !== 'box-button-toggle'
    ) {
      onClickCallback()
    }
  }

  return (
    <div
      className={classnames('c-box', className, {
        'c-box--collapsible': collapsible,
        'c-box--expanded': collapsible && (!collapsed || forceExpanded),
        'c-box--collapsed': collapsible && collapsed && !forceExpanded,
        'c-box--primary': variant === BOX_VARIANT_PRIMARY,
        'c-box--secondary': variant === BOX_VARIANT_SECONDARY,
        'c-box--tertiary': variant === BOX_VARIANT_TERTIARY,
        'c-box--with-toggle': trigger === BOX_TRIGGER_TOGGLE,
        'c-box--clickable': isClickable,
        'c-box--disable': isDisable
      })}
      ref={box}
      data-mu-test={classnames('box', dataAttributeTest)}
      onClick={isClickable ? e => onClickContainer(e) : undefined}
    >
      {(title || collapsible) && (
        <div className='c-box__head'>
          {title && (
            <>
              {titleVariant === BOX_TITLE_BIG && (
                <h3
                  className='c-box__title'
                  onClick={collapsible ? toggle : undefined}
                >
                  {title}
                  {tooltipTitle && (
                    <IconQuestion
                      data-tip={tooltipTitle}
                      className='c-box__tooltip'
                    />
                  )}
                </h3>
              )}
              {titleVariant === BOX_TITLE_SMALL && (
                <strong
                  className='c-box__title'
                  onClick={collapsible ? toggle : undefined}
                >
                  {title}
                  {tooltipTitle && (
                    <IconQuestion
                      data-tip={tooltipTitle}
                      className='c-box__tooltip'
                    />
                  )}
                </strong>
              )}
            </>
          )}

          {}

          {collapsible && (
            <div
              className={classnames('c-box__trigger', {
                'c-box__trigger--default': trigger === BOX_TRIGGER_DEFAULT,
                'c-box__trigger--toggle': trigger === BOX_TRIGGER_TOGGLE,
                'c-box__trigger--collapsed': collapsed
              })}
              data-mu-test='box__trigger'
            >
              {!!trigger && trigger === BOX_TRIGGER_TOGGLE && (
                <Toggle
                  checked={!collapsed || forceExpanded}
                  onChange={e => {
                    !e.target.checked ? collapse() : expand()
                  }}
                  disabled={triggerDisable}
                />
              )}
              {!!trigger && trigger === BOX_TRIGGER_DEFAULT && (
                <button
                  type='button'
                  data-mu-test='box-button-toggle'
                  onClick={toggle}
                />
              )}
            </div>
          )}
        </div>
      )}
      {children && (
        <div className='c-box__content'>
          {collapsible ? (
            <Collapsible collapsed={collapsed && !forceExpanded}>
              {children}
            </Collapsible>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  )
}

Box.defaultProps = {
  collapsible: true,
  variant: BOX_VARIANT_PRIMARY,
  trigger: BOX_TRIGGER_DEFAULT,
  titleVariant: BOX_TITLE_BIG,
  defaultExpanded: false
}

Box.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  title: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  trigger: PropTypes.oneOf([BOX_TRIGGER_DEFAULT, BOX_TRIGGER_TOGGLE]),
  variant: PropTypes.oneOf([
    BOX_VARIANT_PRIMARY,
    BOX_VARIANT_SECONDARY,
    BOX_VARIANT_TERTIARY
  ]),
  collapsible: PropTypes.bool,
  onCollapseChange: PropTypes.func,
  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string
}
